class ChapterNav {
  constructor (context, settings, $, debounce, Ps) {
    // Environment variables
    this.context = context;
    this.settings = settings;
    this.$ = $;
    this.Ps = Ps;

    // General variables
    this.$document = this.$(document);
    this.$window = this.$(window);
    this.$body = this.$('body', this.context);
    this.$main = this.$('main', this.context);
    this.mobileBreakpoint = 767;

    this.$footerPlaceholder = this.$('.js-footer-placeholder', this.context);

    // Chapter nav bar button
    this.$button = this.$('.js-chapter-button', this.context).once();
    this.$buttonText = this.$('.js-chapter-button-text', this.context).once();

    // Chapter navigation
    this.$chapterNav = this.$('.js-chapter-nav', this.context).once();
    this.$chapterNavItems = this.$('.js-chapter-nav-items', this.context).once();
    this.$chapterNavLink = this.$('.js-chapter-nav-link', this.context).once();
    this.$subNavToggle = this.$('.js-chapter-sub-nav-toggle', this.context).once();
    this.$chapterSubNav = this.$('.js-chapter-sub-nav', this.context).once();

    // Active chapters
    this.$chapterHeadings = this.$('.o-content-from-editor h2', this.context).once();
    this.$currentActiveChapter = null; // Stores the currently active chapter
                                       // heading.
    this.$currentChapterTitle = this.$('.js-current-chapter-title', this.context).once();

    // Scrollbars
    //----------------------------------------
    this.chapterScrollbars = [];

    this.addScrollbars();

    // Events
    //----------------------------------------
    // Open the chapter navigation
    this.$button.on('click', $.proxy(this.toggleOpen, this));

    // Open the sub-navigation
    this.$subNavToggle.on('click', $.proxy(this.subNavToggle, this));

    // Close the chapter navigation if an item is clicked
    this.$chapterNavLink.on('click', $.proxy(this.navCloseOnItemClick, this));

    // Update PS size if window resized
    this.$window.on('resize', debounce(() => {
      this.updateScrollbars();
    }));

    this.$window.on('load resize', debounce(() => {
      // Close nav on document click
      this.$document.on('click', $.proxy(this.documentClick, this));

      // Close nav if escape key clicked
      if (this.$window.width() > this.mobileBreakpoint) {
        this.$document.keyup((e) => {
          // Escape Key
          if (e.keyCode === 27) {
            this.navClose();
          }
        });
      }
    }, 300));

    // Prevent chapter nav from clashing with the footer
    this.$window.on('load', () => {
      if ('IntersectionObserver' in window &&
          'IntersectionObserverEntry' in window) {
        if (this.$chapterNav.length) {
          this.observeFooter();
        }
      }
      else {
        // Fallback if IntersectionObserver doesn't work
        this.$chapterNav.removeClass('is-hidden');
      }
    });

    // Update the active chapter on the nav bar and when the chapter nav is
    // opened
    this.$window.on('scroll', debounce(() => {
      this.highlightActiveChapter();
    }, 100));
  }

  highlightActiveChapter () {
    let $current = null;
    const viewportTop = this.$window.scrollTop();
    const viewportBottom = viewportTop + this.$window.height();

    if (this.$chapterHeadings.length) {
      for (let i = 0; i < this.$chapterHeadings.length; i++) {
        const $heading = this.$(this.$chapterHeadings[i]);
        const elementTop = $heading.offset().top;
        const elementBottom = elementTop + $heading.outerHeight();

        if (elementBottom > viewportTop && elementTop < viewportBottom) {
          $current = $heading;
        }

        if (viewportTop < this.$(this.$chapterHeadings[0]).offset().top) {
          this.$currentChapterTitle.text('');
        }
      }
    }

    // If the recently fetched chapter heading is not the same as the
    // previously fetched one, set the new chapter heading as active and make
    // the rest inactive.
    if ($current && $current !== this.$currentActiveChapter) {
      this.removeAllActive();
      this.$currentActiveChapter = $current;
      this.makeActive($current);
    }
  }

  makeActive ($heading) {
    this.$currentChapterTitle.text($heading.text());
    const $headingID = `#${$heading.attr('id')}`;

    if (this.$chapterNavLink.attr('href') === $headingID) {
      this.$chapterNavLink.filter(`[href='${$headingID}']`).addClass('is-active');
    }
  }

  removeAllActive () {
    this.$chapterHeadings.removeClass('is-active');
    this.$chapterNavLink.removeClass('is-active');
  }

  navOpen (e) {
    const $elem = this.$(e.currentTarget);
    const buttonCloseText = this.$buttonText.data('close-text')
    $elem.addClass('is-clicked').attr('aria-expanded', 'true');
    $elem.find('.js-chapter-button-text').text(buttonCloseText);
    this.$main.addClass('unset-z-index');
    this.$body.addClass('has-overlay scroll-locked');
    this.$chapterNavItems.addClass('is-open').attr('aria-hidden', 'false');
    this.$chapterNav.addClass('nav-open');
  }

  navClose () {
    const buttonOpenText = this.$buttonText.data('open-text')
    this.$button.removeClass('is-clicked').attr('aria-expanded', 'false');
    this.$buttonText.text(buttonOpenText);
    this.$main.removeClass('unset-z-index');
    this.$chapterNavItems.removeClass('is-open').attr('aria-hidden', 'true');
    this.$footerPlaceholder.show();
    // Wait for the menu animation to finish
    setTimeout(() => {
      this.$chapterNav.removeClass('nav-open');
    }, 400);

    if (this.$body.hasClass('has-overlay')) {
      this.$body.removeClass('has-overlay scroll-locked');
    }
  }

  navCloseOnItemClick (e) {
    const $elem = this.$(e.currentTarget);
    if (!$elem.hasClass('js-chapter-sub-nav-toggle')) {
      this.navClose();
    }
  }

  toggleOpen (e) {
    const $elem = this.$(e.currentTarget);

    if (!$elem.hasClass('is-clicked')) {
      this.navOpen(e);
    }
    else {
      this.navClose();
    }
  }

  subNavToggle (e) {
    const $elem = this.$(e.currentTarget);
    let ariaAttrValue;

    // Prevent the main chapter link from being clicked
    e.stopPropagation();

    $elem.toggleClass('is-clicked');

    // Toggle the aria-expanded attribute
    ariaAttrValue = $elem.hasClass('is-clicked');
    $elem.attr('aria-expanded', ariaAttrValue);

    $elem.parents('.js-chapter-nav-link').next('.js-chapter-sub-nav').slideToggle().toggleClass('is-open');
  }

  documentClick (e) {
    const $elem = this.$(e.target);

    if (!$elem.closest(this.$chapterNav).length && this.$chapterNav.hasClass('nav-open')) {
      this.navClose();
    }
  }

  addScrollbars () {
    if (this.$chapterNavItems.length) {
      this.chapterScrollbars.push(new this.Ps(this.$chapterNavItems[0]));
    }
  }

  updateScrollbars () {
    if (this.$chapterNavItems.length) {
      this.chapterScrollbars.forEach((ps) => ps.update());
    }
  }

  observeFooter () {
    const footerObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.$chapterNav.addClass('is-hidden');
      }
      else {
        this.$chapterNav.removeClass('is-hidden');
      }
    });

    footerObserver.observe(this.$footerPlaceholder[0]);
  }
}

export default ChapterNav;
